/**
 * Hydrant Bundle Redesign — JavaScript
 * Manages product selection, progress, upsell, and Recharge SDK cart integration.
 *
 * The Recharge SDK (getBundleId) creates the _rb_id that links selected items
 * to the bundle product in the cart. Without this, Recharge can't track which
 * items the customer chose for their bundle.
 */
(function () {
  'use strict';

  // Config injected by Liquid via window.HBR_CONFIG
  var CONFIG = window.HBR_CONFIG || {};
  var BUNDLE_PRODUCT_ID = String(CONFIG.bundleProductId || '');
  var BUNDLE_VARIANT_ID = String(CONFIG.bundleVariantId || '');
  var BUNDLE_HANDLE = CONFIG.bundleHandle || 'fixed-price';
  var BUNDLE_COLLECTION_ID = String(CONFIG.bundleCollectionId || '');
  var SELLING_PLAN_ID = CONFIG.sellingPlanId || null;
  var RECHARGE_TOKEN = CONFIG.rechargeToken || '';
  var PRODUCTS = CONFIG.products || [];
  var MAX_ITEMS = 4;

  // ===== PROVIDER TOGGLE (Skio vs Recharge) =====
  // Sections that don't set useSkio keep the original Recharge behavior.
  var USE_SKIO = CONFIG.useSkio === true;
  var SKIO_BOX_VARIANT_ID = String(CONFIG.skioBoxVariantId || '');
  var SKIO_BOX_SELLING_PLAN_ID = CONFIG.skioBoxSellingPlanId || null;
  var SKIO_PRODUCT_SELLING_PLAN_ID = CONFIG.skioProductSellingPlanId || null;

  var selectedProducts = {}; // variantId -> quantity
  var purchaseType = 'onetime';
  var hasShownConfetti = false;
  var rechargeReady = false;

  // ===== DEBUG LOGGING =====
  function updateDebug(key, value) {
    console.log('[HBR] ' + key + ':', value);
  }

  // ===== RECHARGE SDK INIT =====
  function initRecharge() {
    if (typeof recharge === 'undefined') {
      updateDebug('sdk-loaded', 'no');
      updateDebug('sdk-status', 'SDK script not loaded yet');
      return false;
    }
    updateDebug('sdk-loaded', 'yes');

    if (!RECHARGE_TOKEN) {
      updateDebug('sdk-status', 'FAILED: No token configured');
      return false;
    }
    try {
      recharge.init({
        storeIdentifier: 'hydrantstore.myshopify.com',
        storefrontAccessToken: RECHARGE_TOKEN,
        loginRetryFn: function () {
          return recharge.auth.loginShopifyAppProxy();
        }
      });
      rechargeReady = true;
      updateDebug('sdk-status', 'INITIALIZED OK');
      return true;
    } catch (e) {
      updateDebug('sdk-status', 'INIT FAILED: ' + e.message);
      return false;
    }
  }

  // Retry SDK init in case the script loads after ours
  function waitForRecharge(retries) {
    if (rechargeReady) return;
    if (retries <= 0) {
      updateDebug('sdk-status', 'FAILED: SDK never loaded after 10 retries');
      return;
    }
    if (initRecharge()) return;
    setTimeout(function () { waitForRecharge(retries - 1); }, 500);
  }

  // ===== HELPERS =====
  function getTotalSelected() {
    return Object.values(selectedProducts).reduce(function (s, q) { return s + q; }, 0);
  }

  function getProductById(variantId) {
    return PRODUCTS.find(function (p) { return String(p.variantId) === String(variantId); });
  }

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // ===== CATEGORY FILTERING =====
  function filterCategory(cat, btn) {
    document.querySelectorAll('.hbr-category-tab').forEach(function (t) {
      t.classList.remove('active');
    });
    btn.classList.add('active');

    document.querySelectorAll('.hbr-product-card').forEach(function (card) {
      if (cat === 'all') {
        card.classList.remove('hbr-hidden');
      } else {
        var cats = (card.dataset.categories || '').split(',');
        if (cats.indexOf(cat) >= 0) {
          card.classList.remove('hbr-hidden');
        } else {
          card.classList.add('hbr-hidden');
        }
      }
    });
  }

  // ===== QUANTITY MANAGEMENT =====
  function changeQty(variantId, delta) {
    var cur = selectedProducts[variantId] || 0;
    var total = getTotalSelected();
    var nq = cur + delta;

    if (nq < 0) return;
    if (delta > 0 && total >= MAX_ITEMS) {
      var pc = document.querySelector('.hbr-progress-card');
      if (pc) {
        pc.style.animation = 'none';
        pc.offsetHeight; // force reflow
        pc.style.animation = 'hbr-shake 0.4s ease';
      }
      return;
    }

    if (nq === 0) {
      delete selectedProducts[variantId];
    } else {
      selectedProducts[variantId] = nq;
    }
    updateUI();
  }

  // ===== UI UPDATE =====
  function updateUI() {
    var total = getTotalSelected();
    updateProductCards();
    updateProgress(total);
    updateStickyBar(total);
    updateUpsell(total);
  }

  function updateProductCards() {
    document.querySelectorAll('.hbr-product-card').forEach(function (card) {
      var vid = card.dataset.variantId;
      var qty = selectedProducts[vid] || 0;
      var sel = qty > 0;
      var metaEl = card.querySelector('.hbr-product-meta');

      card.classList.toggle('selected', sel);

      if (sel) {
        metaEl.innerHTML =
          '<span class="hbr-product-servings">' + (card.dataset.servings || '30 sticks') + '</span>' +
          '<div class="hbr-product-qty-controls">' +
            '<button class="hbr-qty-btn" data-action="minus" data-vid="' + vid + '">\u2212</button>' +
            '<span class="hbr-qty-value">' + qty + '</span>' +
            '<button class="hbr-qty-btn" data-action="plus" data-vid="' + vid + '">+</button>' +
          '</div>';
      } else {
        metaEl.innerHTML =
          '<span class="hbr-product-servings">' + (card.dataset.servings || '30 sticks') + '</span>' +
          '<button class="hbr-product-add-btn" data-action="add" data-vid="' + vid + '">+</button>';
      }
    });
  }

  function updateProgress(total) {
    var pct = (total / MAX_ITEMS) * 100;
    var fill = document.getElementById('hbr-progress-fill');
    if (fill) {
      fill.style.width = pct + '%';
      fill.className = 'hbr-progress-bar-fill' + (total >= MAX_ITEMS ? ' complete' : '');
    }

    var countEl = document.getElementById('hbr-selected-count');
    if (countEl) countEl.textContent = total;

    var card = document.getElementById('hbr-progress-card');
    var title = document.getElementById('hbr-progress-title');
    if (card && title) {
      if (total >= MAX_ITEMS) {
        card.classList.add('bundle-complete');
        title.innerHTML = 'Your bundle is <span class="hbr-accent-underline">ready!</span>';
      } else {
        card.classList.remove('bundle-complete');
        title.innerHTML = 'Build Your <span class="hbr-accent-underline">Bundle</span>';
      }
    }

    // Update slots
    var slots = document.querySelectorAll('.hbr-progress-slot');
    var entries = [];
    Object.keys(selectedProducts).forEach(function (vid) {
      var p = getProductById(vid);
      if (!p) return;
      for (var i = 0; i < selectedProducts[vid]; i++) {
        entries.push(p);
      }
    });

    slots.forEach(function (s, i) {
      var isFree = i >= 2;
      if (i < entries.length) {
        s.className = 'hbr-progress-slot filled' + (isFree ? ' free-slot' : '');
        var img = entries[i].image;
        if (img) {
          s.innerHTML = '<img src="' + img + '" alt="" style="width:36px;height:36px;object-fit:contain;border-radius:4px;">';
        } else {
          s.innerHTML = '<span style="font-size:14px;font-weight:800;color:var(--brand-teal);">\u2713</span>';
        }
      } else {
        s.className = 'hbr-progress-slot' + (isFree ? ' free-slot' : '');
        s.innerHTML = '+';
      }
    });
  }

  function updateUpsell(total) {
    var upsell = document.getElementById('hbr-upsell-section');
    var spacer = document.getElementById('hbr-bottom-spacer');
    if (!upsell) return;

    if (total >= MAX_ITEMS) {
      upsell.classList.add('visible');
      if (spacer) spacer.classList.add('hidden');
      if (!hasShownConfetti) {
        hasShownConfetti = true;
        launchConfetti();
        // Open upsell as bottom sheet if available, otherwise scroll inline
        setTimeout(function () {
          if (typeof openUpsellSheet === 'function') {
            openUpsellSheet();
          } else {
            upsell.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 400);
      }
    } else {
      upsell.classList.remove('visible');
      if (spacer) spacer.classList.remove('hidden');
      hasShownConfetti = false;
      // Close sheet if open
      if (typeof closeUpsellSheet === 'function') closeUpsellSheet();
    }
  }

  function updateStickyBar(total) {
    var countEl = document.getElementById('hbr-sticky-count');
    if (countEl) countEl.textContent = total;

    var btn = document.getElementById('hbr-add-to-cart-btn');
    if (!btn) return;

    // Always clear inline styles from success/error states
    btn.style.background = '';

    if (total >= MAX_ITEMS) {
      btn.disabled = false;
      btn.classList.add('ready');
      btn.classList.remove('choose-flavors');
      btn.textContent = purchaseType === 'subscribe'
        ? 'Subscribe & Add to Cart \u2014 $80.99'
        : 'Add Bundle to Cart \u2014 $89.99';
    } else {
      btn.disabled = false;
      btn.classList.remove('ready');
      btn.classList.add('choose-flavors');
      var r = MAX_ITEMS - total;
      btn.textContent = 'Select ' + r + ' Flavor' + (r > 1 ? 's' : '');
    }

    var priceEl = document.getElementById('hbr-sticky-price');
    var perunitEl = document.getElementById('hbr-sticky-perunit');
    var savingsEl = document.getElementById('hbr-sticky-savings');

    if (purchaseType === 'subscribe') {
      if (priceEl) priceEl.textContent = '$80.99';
      if (perunitEl) perunitEl.textContent = '$0.67/stick pack \u2022 FREE shipping \u2022 Cancel anytime';
      if (savingsEl) savingsEl.textContent = '59% OFF';
    } else {
      if (priceEl) priceEl.textContent = '$89.99';
      if (perunitEl) perunitEl.textContent = '$0.75/stick pack \u2022 FREE shipping';
      if (savingsEl) savingsEl.textContent = '54% OFF';
    }
  }

  // ===== PURCHASE TYPE TOGGLE =====
  function selectPurchaseType(type, el) {
    purchaseType = type;
    document.querySelectorAll('.hbr-upsell-option').forEach(function (o) {
      o.classList.remove('active');
    });
    el.classList.add('active');

    var callout = document.getElementById('hbr-upsell-callout');
    if (callout) {
      callout.textContent = type === 'subscribe'
        ? 'You save $118.97 with Subscribe & Save! ($9 more than one-time)'
        : 'You save $109.97 with this bundle!';
    }
    updateStickyBar(getTotalSelected());
  }

  // ===== ADD TO CART =====
  async function addToCart() {
    if (getTotalSelected() < MAX_ITEMS) return;

    var btn = document.getElementById('hbr-add-to-cart-btn');
    if (!btn) return;
    btn.textContent = 'Adding to cart...';
    btn.disabled = true;
    btn.classList.remove('ready');

    try {
      // Build Contents string from selected products (e.g. "1x Lemon Lime 2x Blood Orange").
      // Shared by both providers.
      var contentsParts = [];
      Object.keys(selectedProducts).forEach(function (vid) {
        var p = getProductById(vid);
        if (!p) return;
        contentsParts.push(selectedProducts[vid] + 'x ' + p.name);
      });
      var contentsStr = contentsParts.join(' ');

      updateDebug('purchase-type', purchaseType);
      updateDebug('provider', USE_SKIO ? 'skio' : 'recharge');

      var bundleItem;

      if (USE_SKIO) {
        // ===== SKIO STATIC BUILD-A-BOX PATH =====
        // The box parent variant is the single priced line; children ride as
        // line-item properties (mirrors Skio's own picker output: paired
        // human-readable + _pvgid keys, plus the _isClassicBoxV2 flag).
        // No Recharge SDK needed.
        var properties = { _isClassicBoxV2: true, 'Contents': contentsStr };
        Object.keys(selectedProducts).forEach(function (vid) {
          var qty = selectedProducts[vid];
          var sp = getProductById(vid);
          var name = sp ? sp.name : vid;
          properties[name] = qty;                                      // human-readable (display only)
          properties['_pvgid://shopify/ProductVariant/' + vid] = qty;  // canonical Skio key
        });
        bundleItem = {
          id: parseInt(SKIO_BOX_VARIANT_ID),
          quantity: 1,
          properties: properties
        };
        if (purchaseType === 'subscribe' && SKIO_BOX_SELLING_PLAN_ID) {
          bundleItem.selling_plan = SKIO_BOX_SELLING_PLAN_ID;
          updateDebug('last-action', 'Skio box, adding with selling_plan ' + SKIO_BOX_SELLING_PLAN_ID);
        } else {
          updateDebug('last-action', 'Skio box, adding one-time');
        }
      } else {
        // ===== RECHARGE SDK PATH =====
        // Build selections array for Recharge SDK
        var selections = [];
        Object.keys(selectedProducts).forEach(function (vid) {
          var p = getProductById(vid);
          if (!p) return;
          selections.push({
            collectionId: BUNDLE_COLLECTION_ID,
            externalProductId: String(p.productId),
            externalVariantId: String(vid),
            quantity: selectedProducts[vid]
          });
        });

        var bundle = {
          externalVariantId: BUNDLE_VARIANT_ID,
          externalProductId: BUNDLE_PRODUCT_ID,
          selections: selections
        };

        updateDebug('bundle-object', JSON.stringify(bundle));

        if (!rechargeReady || typeof recharge === 'undefined') {
          // Try one more init attempt before giving up
          if (!initRecharge()) {
            var errMsg = 'Recharge SDK not initialized. rechargeReady=' + rechargeReady + ', typeof recharge=' + (typeof recharge);
            updateDebug('last-error', errMsg);
            btn.textContent = 'SDK not ready \u2014 try again in a moment';
            btn.disabled = false;
            setTimeout(function () { updateStickyBar(getTotalSelected()); }, 3000);
            return;
          }
        }

        // Step 1: Validate bundle with Recharge SDK
        updateDebug('last-action', 'Calling validateBundle...');
        var validation;
        try {
          validation = await recharge.bundle.validateBundle(bundle);
          updateDebug('validation', String(validation));
        } catch (valErr) {
          updateDebug('validation', 'THREW ERROR: ' + valErr.message);
          updateDebug('last-error', 'validateBundle threw: ' + valErr.message);
          btn.textContent = 'Validation error: ' + valErr.message;
          btn.disabled = false;
          setTimeout(function () { updateStickyBar(getTotalSelected()); }, 5000);
          return;
        }

        if (validation !== true) {
          updateDebug('last-error', 'validateBundle returned: ' + validation);
          btn.textContent = 'Bundle invalid: ' + validation;
          btn.disabled = false;
          setTimeout(function () { updateStickyBar(getTotalSelected()); }, 5000);
          return;
        }

        // Step 2: Get bundle ID (fixed-price bundle — always use getBundleId)
        updateDebug('last-action', 'Calling getBundleId...');
        var rbId = await recharge.bundle.getBundleId(bundle);
        updateDebug('rb-id', rbId);

        bundleItem = {
          id: parseInt(BUNDLE_VARIANT_ID),
          quantity: 1,
          properties: { _rb_id: rbId, 'Contents': contentsStr }
        };

        // Add selling plan for subscribe
        if (purchaseType === 'subscribe' && SELLING_PLAN_ID) {
          bundleItem.selling_plan = SELLING_PLAN_ID;
          updateDebug('last-action', 'getBundleId succeeded, adding with selling_plan ' + SELLING_PLAN_ID);
        } else {
          updateDebug('last-action', 'getBundleId succeeded, adding one-time');
        }
      }

      var cartData = { items: [bundleItem] };

      // Step 3: Clear existing cart, then add bundle
      updateDebug('last-action', 'Clearing cart before adding bundle...');
      await fetch(cartRoot() + 'cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      updateDebug('last-action', 'Adding bundle to cart...');
      updateDebug('cart-data', JSON.stringify(cartData));

      var response = await fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      });

      if (!response.ok) {
        var errorBody = await response.text();
        throw new Error('Cart add failed (' + response.status + '): ' + errorBody);
      }

      var cartResponse = await response.json();
      updateDebug('last-action', 'Cart add succeeded!');
      updateDebug('cart-response', JSON.stringify(cartResponse));

      // Success
      btn.textContent = '\u2713 Added! Opening cart...';
      btn.style.background = 'linear-gradient(135deg, #34c759, #2db84d)';
      launchConfetti();

      setTimeout(function () {
        try {
          if (typeof Rebuy !== 'undefined' && Rebuy.SmartCart) {
            Rebuy.SmartCart.show();
          } else if (document.querySelector('cart-drawer')) {
            document.querySelector('cart-drawer').open();
          } else {
            window.location.href = '/cart';
          }
        } catch (e) {
          console.error('SmartCart error:', e);
          window.location.href = '/cart';
        }
        // Reset button so user can add again after closing the cart
        btn.disabled = false;
        btn.style.background = '';
        updateStickyBar(getTotalSelected());
      }, 1200);

    } catch (err) {
      updateDebug('last-error', err.message + (err.stack ? '\n' + err.stack : ''));
      btn.textContent = 'Error: ' + err.message.substring(0, 60);
      setTimeout(function () { updateStickyBar(getTotalSelected()); }, 5000);
    } finally {
      btn.disabled = false;
      btn.style.background = '';
    }
  }

  // ===== CONFETTI =====
  function launchConfetti() {
    var canvas = document.getElementById('hbr-confetti-canvas');
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    var colors = ['#00b4a0', '#ff6b35', '#f5a623', '#34c759', '#ff5e8a', '#7cb9c7', '#7c5cbf'];
    var pieces = [];
    for (var i = 0; i < 120; i++) {
      pieces.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 200,
        w: 6 + Math.random() * 6,
        h: 4 + Math.random() * 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 4,
        vy: 2 + Math.random() * 4,
        rot: Math.random() * Math.PI * 2,
        rv: (Math.random() - 0.5) * 0.15,
        life: 1
      });
    }
    var frame = 0;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      var alive = false;
      pieces.forEach(function (p) {
        if (p.life <= 0) return;
        alive = true;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.08;
        p.rot += p.rv;
        if (frame > 40) p.life -= 0.012;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      frame++;
      if (alive) requestAnimationFrame(animate);
      else ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    animate();
  }

  // ===== EVENT DELEGATION =====
  function handleClicks(e) {
    var target = e.target.closest('[data-action]');
    if (!target) return;

    var action = target.dataset.action;
    var vid = target.dataset.vid;

    e.stopPropagation();

    if (action === 'add' || action === 'plus') {
      changeQty(vid, 1);
    } else if (action === 'minus') {
      changeQty(vid, -1);
    }
  }

  function handleCategoryClick(e) {
    var tab = e.target.closest('.hbr-category-tab');
    if (!tab) return;
    var cat = tab.dataset.category;
    filterCategory(cat, tab);
  }

  function handleUpsellClick(e) {
    var option = e.target.closest('.hbr-upsell-option');
    if (!option) return;
    var type = option.dataset.type;
    selectPurchaseType(type, option);
  }

  function handleCartClick() {
    var btn = document.getElementById('hbr-add-to-cart-btn');
    if (btn && btn.classList.contains('choose-flavors')) {
      var target = document.getElementById('hbr-progress-card');
      if (target) target.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    addToCart();
  }

  // ===== INIT =====
  function init() {
    // Try to init Recharge SDK, with retries if it hasn't loaded yet.
    // Skio provider doesn't use the Recharge SDK, so skip it entirely.
    if (!USE_SKIO) {
      if (!initRecharge()) {
        waitForRecharge(10); // retry up to 10 times (5 seconds)
      }
    }

    // Event delegation for product grid
    var grid = document.querySelector('.hbr-product-grid');
    if (grid) grid.addEventListener('click', handleClicks);

    // Category tabs
    var tabs = document.querySelector('.hbr-category-tabs');
    if (tabs) tabs.addEventListener('click', handleCategoryClick);

    // Upsell options
    var upsellToggle = document.querySelector('.hbr-upsell-toggle');
    if (upsellToggle) upsellToggle.addEventListener('click', handleUpsellClick);

    // Cart button
    var cartBtn = document.getElementById('hbr-add-to-cart-btn');
    if (cartBtn) cartBtn.addEventListener('click', handleCartClick);

    // Initial UI state
    updateUI();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
